import {Tooltip} from 'antd';
import v2 from '../v2/instrumentV2.module.scss';
import {instrumentSupports} from './instrumentKeys';

/**
 * The inventory position, as table columns.
 *
 * Lives here rather than in either table because two screens show the same
 * position — Asset classes → Funds/Bonds, and Multi Asset → Instruments — and
 * they had drifted: one collapsed the whole position into a health bar with the
 * numbers in a tooltip, the other did not show it at all. A figure an operator
 * has to hover to read is a figure they cannot compare down a column, which is
 * the reason to have a table.
 *
 * Only the types that can actually hold a position get these columns. On the
 * rest every cell would read the same dash, and a column of dashes is worse
 * than an absent column: it implies the value is missing rather than
 * inapplicable.
 */

const number = (value, digits = 2) =>
  value == null
    ? null
    : new Intl.NumberFormat('en-US', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : digits,
        maximumFractionDigits: digits,
      }).format(value);

/** One mark for "no position", used in every cell of the group. */
const Blank = () => <span className={v2.healthEmpty}>—</span>;

const Amount = ({value, currency}) => {
  const formatted = number(value);
  if (formatted == null) return <Blank />;
  return (
    <span className={v2.cellTicker}>
      {currency ? `${currency} ` : ''}
      {formatted}
    </span>
  );
};

const healthColour = pct =>
  pct < 50 ? '#bf3320' : pct < 75 ? '#a86a12' : '#12805c';

/**
 * Builds the grouped column set, or an empty array when the type cannot hold
 * inventory. Spread it into a table's `columns`.
 */
export const inventoryColumns = instrumentKey => {
  if (!instrumentSupports(instrumentKey, 'inventory')) return [];

  return [
    {
      title: 'Inventory position',
      key: 'inventoryPosition',
      children: [
        {
          title: 'Available units',
          key: 'availableUnits',
          align: 'right',
          width: 130,
          render: (text, record) => {
            const value = record?.inventoryPosition?.availableUnits;
            return value == null ? (
              <Blank />
            ) : (
              <span className={v2.cellTicker}>{number(value)}</span>
            );
          },
        },
        {
          title: 'Average unit cost',
          key: 'averageUnitCost',
          align: 'right',
          width: 150,
          render: (text, record) => (
            <Amount
              value={record?.inventoryPosition?.averageUnitCost}
              currency={record?.currency?.currencyCode}
            />
          ),
        },
        {
          title: 'Low unit threshold',
          key: 'lowUnitThreshold',
          align: 'right',
          width: 150,
          render: (text, record) => {
            const value = record?.inventoryPosition?.lowUnitThreshold;
            return value == null ? (
              <Blank />
            ) : (
              <span className={v2.cellTicker}>{number(value, 0)}</span>
            );
          },
        },
        {
          title: 'Auto-buy threshold',
          key: 'autoBuyThresholdAmount',
          align: 'right',
          width: 155,
          render: (text, record) => (
            <Amount
              value={record?.inventoryPosition?.autoBuyThresholdAmount}
              currency={record?.currency?.currencyCode}
            />
          ),
        },
        {
          title: 'Health',
          key: 'health',
          width: 150,
          render: (text, record) => {
            const position = record?.inventoryPosition;
            if (!position) return <Blank />;
            const pct = position.healthPercentage ?? 0;
            return (
              <Tooltip
                title={`${number(position.availableUnits)} units against a low
                  threshold of ${number(position.lowUnitThreshold, 0)}`}>
                <div className={v2.health}>
                  <div className={v2.healthBar}>
                    <span
                      style={{
                        width: `${Math.min(100, pct)}%`,
                        background: healthColour(pct),
                      }}
                    />
                  </div>
                  <span className={v2.healthValue}>{pct.toFixed(0)}%</span>
                </div>
              </Tooltip>
            );
          },
        },
        {
          title: 'Status',
          key: 'inventoryStatus',
          width: 130,
          render: (text, record) => {
            const position = record?.inventoryPosition;
            // Three states, not two. "Disabled" on an instrument that has no
            // position at all says trading was turned off, when in fact nothing
            // was ever set up — which is the difference between a decision and
            // an omission.
            if (!position)
              return (
                <span className={`${v2.chip} ${v2.chipMute}`}>
                  Not configured
                </span>
              );
            return (
              <span
                className={`${v2.chip} ${
                  position.enabledForTrading ? v2.chipOk : v2.chipStop
                }`}>
                {position.enabledForTrading ? 'Enabled' : 'Disabled'}
              </span>
            );
          },
        },
      ],
    },
  ];
};

/** Combined width of the six inventory columns above. */
export const INVENTORY_GROUP_WIDTH = 130 + 150 + 150 + 155 + 130 + 130;

/**
 * How wide the table needs to be once the group is on.
 *
 * `baseWidth` is what the caller's own columns need. Antd hands the fixed
 * widths out first and splits whatever `scroll.x` leaves between the columns
 * that declared none — so an under-stated total does not scroll, it crushes
 * Status and Currency into two-line stubs. Every column in these two tables now
 * declares a width, and this is their sum.
 */
export const inventoryScrollWidth = (instrumentKey, baseWidth) =>
  instrumentSupports(instrumentKey, 'inventory')
    ? baseWidth + INVENTORY_GROUP_WIDTH
    : undefined;

/**
 * Pins the identifying columns while the inventory group scrolls, so a row of
 * numbers never loses the instrument it belongs to.
 *
 * Only applied when the table actually scrolls — antd needs `scroll.x` for a
 * fixed column to behave, and pinning one in a table that fits produces a
 * stray shadow over nothing.
 */
export const stickyLeft = (instrumentKey, width) =>
  instrumentSupports(instrumentKey, 'inventory')
    ? {fixed: 'left', width}
    : {width};
